import type { Document } from "@/types/documents";
import type {
  TimelineItem,
  TimelineCluster,
  TimelineData,
  ZoomLevel,
  TimelineDateMarker,
} from "@/domains/documents/types/timeline";
import {
  getDocumentTimestamp,
  getMinCardGap,
  ZOOM_CONFIG,
} from "@/domains/documents/types/timeline";
import type { TimelineBucket, TimelineGranularity } from "@/stores/documents/repository";
import { zoomToGranularity } from "@/stores/documents/repository";
import { format, type Locale } from "date-fns";

/**
 * Constant vertical gap (px, centre to centre) between two rendered timeline
 * entries — a cluster card or a document card. Deliberately NOT scaled by the
 * time gap between entries: how far apart two entries are in time is read off
 * the date labels, not off the whitespace, so a two-year jump gets the same
 * compact gap as a two-minute one. Cards alternate sides, so same-side
 * neighbours sit 2 × this apart.
 */
export const TIMELINE_ENTRY_GAP = 72;

/**
 * Slots a bucket that renders one card per document occupies.
 *
 * Its cards all stay on the bucket's own side of the stem — a day's documents
 * belong together, so they must not straddle it — which means consecutive
 * cards would otherwise land one 72px slot apart and overlap. Skipping the
 * opposite-side slot between them restores the 2 × gap every same-side pair
 * has elsewhere, and keeps the count odd so the next bucket still flips side.
 */
export function bucketSlotCount(documentCount: number): number {
  return Math.max(1, documentCount * 2 - 1);
}

/** First entry's offset from the top of the timeline content area. */
const TIMELINE_START_OFFSET = 32;

/**
 * Whether a bucket renders its documents as individual cards: single-document
 * buckets always do, and at day zoom every bucket does — the Days band shows
 * one card per document, never a cluster tile standing in for a day.
 * Shared by the position maths and the renderer so the reserved slots and the
 * rendered cards can never disagree.
 */
export function bucketShowsDocumentCards(documentCount: number, zoomLevel: number): boolean {
  if (documentCount === 1) return true;
  return zoomToGranularity(zoomLevel) === "day";
}

/**
 * Convert documents to timeline items with timestamps
 */
export function documentsToTimelineItems(documents: Document[]): TimelineItem[] {
  return documents.map((doc) => ({
    id: doc.id,
    document: doc,
    timestamp: getDocumentTimestamp(doc),
  }));
}

/**
 * Main clustering function - groups documents based on zoom level
 * Uses density-based positioning to collapse large gaps
 */
export function clusterDocuments(
  documents: Document[],
  zoomLevel: ZoomLevel,
  containerHeight: number
): TimelineData {
  if (documents.length === 0) {
    return {
      clusters: [],
      timeRange: {
        start: new Date(),
        end: new Date(),
        durationMs: 0,
      },
      totalDocuments: 0,
    };
  }

  // Convert to timeline items and sort by timestamp (newest first for top-down display)
  const items = documentsToTimelineItems(documents).sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
  );

  // Calculate time range
  const timestamps = items.map((item) => item.timestamp.getTime());
  const minTime = Math.min(...timestamps);
  const maxTime = Math.max(...timestamps);
  const timeRange = {
    start: new Date(minTime),
    end: new Date(maxTime),
    durationMs: maxTime - minTime || 1, // Prevent division by zero
  };

  // Continuous zoom scaling - exponential for smooth feel
  // zoomLevel 0 = 1x, 50 = ~7x, 100 = ~50x
  const zoomScale = Math.pow(1.04, zoomLevel); // 1.04^100 ≈ 50

  // Get minimum card gap for current zoom level
  const minCardGap = getMinCardGap(zoomLevel);

  // Calculate base height needed for density-based positioning
  // More zoom = more spread between documents
  const baseItemSpacing = minCardGap * 1.2; // Base spacing between items

  // Position items using density-based algorithm
  // This compresses large time gaps while maintaining relative order
  const itemsWithPositions = calculateDensityPositions(
    items,
    timeRange,
    zoomLevel,
    baseItemSpacing
  );

  // Cluster items that are too close together
  const clusters = clusterByPixelProximity(itemsWithPositions, minCardGap);

  return { clusters, timeRange, totalDocuments: documents.length };
}

/**
 * Calculate positions using fluid density-based algorithm
 *
 * Key insight: Timeline should always feel fluid with no huge empty gaps.
 * Spacing = base spacing + small time-aware bonus (log-scaled)
 *
 * At low zoom: items close together, time differences add slight variation
 * At high zoom: items more spread, time differences more pronounced
 */
function calculateDensityPositions(
  items: TimelineItem[],
  timeRange: { start: Date; end: Date; durationMs: number },
  zoomLevel: number,
  baseSpacing: number
): ItemWithPosition[] {
  if (items.length === 0) return [];
  if (items.length === 1) {
    return [
      {
        ...items[0],
        normalizedPosition: 0.5,
        pixelPosition: 60,
      },
    ];
  }

  const result: ItemWithPosition[] = [];

  // Zoom factor: 0 at min zoom, 1 at max zoom
  const zoomFactor = zoomLevel / ZOOM_CONFIG.MAX;

  // Calculate time gaps between consecutive items (in milliseconds)
  const timeGaps: number[] = [];
  for (let i = 1; i < items.length; i++) {
    const gap = items[i - 1].timestamp.getTime() - items[i].timestamp.getTime();
    timeGaps.push(Math.max(gap, 0));
  }

  // Base spacing between items (ensures fluid feel, no huge gaps)
  // IMPORTANT: Base spacing < clustering threshold, so items with 0 time gap cluster
  // Time bonus pushes different-time items beyond threshold
  // At low zoom: 30px base < 40px threshold
  // At high zoom: 50px base < 70px threshold
  const minBaseSpacing = 30;
  const maxBaseSpacing = 50;
  const currentBaseSpacing = minBaseSpacing + (maxBaseSpacing - minBaseSpacing) * zoomFactor;

  // Time bonus factor - how much extra space for time differences
  // Uses log scale so a 2-month gap isn't 1000x bigger than a 5-minute gap
  // At low zoom: minimal bonus (clustering by year/month)
  // At high zoom: aggressive bonus (even 1 min apart = no cluster)
  // Math: log(2) * 35 ≈ 24px, so 1-min gap = 50 + 24 = 74px > 70px threshold
  const minBonusFactor = 5;
  const maxBonusFactor = 35;
  const bonusFactor =
    minBonusFactor + (maxBonusFactor - minBonusFactor) * Math.pow(zoomFactor, 1.5);

  // Reference: 1 minute in milliseconds for log scaling
  const ONE_MINUTE = 60 * 1000;

  // Calculate pixel positions
  let currentPosition = 40; // Start offset

  // First item (newest)
  result.push({
    ...items[0],
    normalizedPosition: 0,
    pixelPosition: currentPosition,
  });

  // Remaining items
  for (let i = 1; i < items.length; i++) {
    const timeGapMs = timeGaps[i - 1];

    // Log-scaled time bonus (adds slight variation based on time gap)
    // log(1 + gap/minute) so even small gaps register, large gaps don't dominate
    const timeBonus = Math.log(1 + timeGapMs / ONE_MINUTE) * bonusFactor;

    // Total spacing = base + time bonus
    const spacing = currentBaseSpacing + timeBonus;

    currentPosition += spacing;

    const normalizedPos = i / (items.length - 1);

    result.push({
      ...items[i],
      normalizedPosition: normalizedPos,
      pixelPosition: currentPosition,
    });
  }

  return result;
}

interface ItemWithPosition extends TimelineItem {
  normalizedPosition: number;
  pixelPosition: number;
}

/**
 * Cluster items based on their pixel proximity
 */
function clusterByPixelProximity(items: ItemWithPosition[], minGap: number): TimelineCluster[] {
  if (items.length === 0) return [];

  const clusters: TimelineCluster[] = [];
  let currentCluster: ItemWithPosition[] = [items[0]];

  for (let i = 1; i < items.length; i++) {
    const prevItem = items[i - 1];
    const currItem = items[i];
    const gap = Math.abs(currItem.pixelPosition - prevItem.pixelPosition);

    if (gap < minGap) {
      // Items are too close - add to current cluster
      currentCluster.push(currItem);
    } else {
      // Start a new cluster
      clusters.push(createCluster(currentCluster, clusters.length));
      currentCluster = [currItem];
    }
  }

  // Don't forget the last cluster
  clusters.push(createCluster(currentCluster, clusters.length));

  return clusters;
}

/**
 * Create a TimelineCluster from a group of items
 */
function createCluster(items: ItemWithPosition[], index: number): TimelineCluster {
  const timestamps = items.map((i) => i.timestamp.getTime());
  const startTime = new Date(Math.min(...timestamps));
  const endTime = new Date(Math.max(...timestamps));

  // Use average position for the cluster
  const avgPosition = items.reduce((sum, i) => sum + i.normalizedPosition, 0) / items.length;
  const avgPixelPosition = items.reduce((sum, i) => sum + i.pixelPosition, 0) / items.length;

  return {
    id: items.length === 1 ? `single-${items[0].id}` : `cluster-${index}`,
    type: items.length === 1 ? "single" : "cluster",
    items,
    startTime,
    endTime,
    normalizedPosition: avgPosition,
    pixelPosition: avgPixelPosition,
    side: index % 2 === 0 ? "left" : "right",
  };
}

/**
 * Generate date markers for the timeline stem - only at cluster positions
 * This ensures markers appear where documents actually exist
 */
export function generateDateMarkers(
  timeRange: { start: Date; end: Date; durationMs: number },
  _zoomLevel: ZoomLevel,
  _containerHeight: number,
  clusters?: TimelineCluster[]
): TimelineDateMarker[] {
  const markers: TimelineDateMarker[] = [];

  // If no clusters provided, return empty
  if (!clusters || clusters.length === 0) return markers;

  // Calculate time span to determine date format
  const daysSpan = timeRange.durationMs / (24 * 60 * 60 * 1000);
  const yearsSpan = daysSpan / 365;

  // Determine format based on time span
  let formatStr: string;
  if (yearsSpan > 2) {
    formatStr = "yyyy";
  } else if (yearsSpan > 0.5) {
    formatStr = "MMM yyyy";
  } else if (daysSpan > 30) {
    formatStr = "MMM d";
  } else if (daysSpan > 1) {
    formatStr = "MMM d";
  } else {
    formatStr = "h:mm a";
  }

  // Generate a marker for each cluster position
  // But deduplicate by formatted label to avoid repeats
  const seenLabels = new Set<string>();

  for (const cluster of clusters) {
    // Use the middle of the cluster's time range
    const midTime = new Date((cluster.startTime.getTime() + cluster.endTime.getTime()) / 2);
    const label = format(midTime, formatStr);

    // Skip if we already have this label
    if (seenLabels.has(label)) continue;
    seenLabels.add(label);

    markers.push({
      date: midTime,
      label,
      normalizedPosition: cluster.normalizedPosition,
      pixelPosition: cluster.pixelPosition,
      isMinor: false,
    });
  }

  return markers;
}

/**
 * Calculate the time range to zoom to when expanding a cluster
 */
export function getClusterZoomRange(cluster: TimelineCluster): { start: Date; end: Date } {
  // Add 10% padding on each side
  const durationMs = cluster.endTime.getTime() - cluster.startTime.getTime();
  const padding = Math.max(durationMs * 0.1, 60 * 60 * 1000); // At least 1 hour padding

  return {
    start: new Date(cluster.startTime.getTime() - padding),
    end: new Date(cluster.endTime.getTime() + padding),
  };
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * The date range a cluster card prints, at the granularity it was bucketed by.
 *
 * A bucket ENDS at the next one's start: `date_trunc(...) + 1 <unit>`,
 * exclusive, both from `get_document_timeline_summary` and from the client-side
 * bucketing that mirrors it (`timelineContentMatchBucketing`). The label names
 * days, so it has to print the last day the bucket COVERS — a day back from
 * that boundary. Formatting the boundary itself renders a one-day cluster as
 * "Aug 26 - Aug 27" and a week as eight days. A whole day back rather than a
 * millisecond, so the last day is still the right one once date-fns formats it
 * in the viewer's own timezone (bucket edges are UTC midnights).
 */
export function formatClusterDateRange(
  cluster: { startTime: Date; endTime: Date },
  granularity: TimelineGranularity | undefined,
  locale?: Locale
): string {
  if (granularity === "year") return format(cluster.startTime, "yyyy");
  if (granularity === "month") return format(cluster.startTime, "MMM yyyy", { locale });

  const lastDay = new Date(cluster.endTime.getTime() - MS_PER_DAY);
  if (format(cluster.startTime, "yyyy-MM-dd") === format(lastDay, "yyyy-MM-dd")) {
    return format(cluster.startTime, "MMM d, yyyy", { locale });
  }
  return (
    format(cluster.startTime, "MMM d", { locale }) +
    " - " +
    format(lastDay, "MMM d, yyyy", { locale })
  );
}

// =============================================================================
// Server-Side Aggregation Functions
// Convert server-side TimelineBucket data to UI-renderable TimelineCluster format
// =============================================================================

/**
 * Server-side bucket to timeline cluster data structure.
 * Used when timeline data comes from server-side aggregation (RPC functions)
 * rather than client-side clustering of all documents.
 */
export interface ServerTimelineCluster {
  id: string;
  type: "single" | "cluster";
  /** Number of documents in this bucket (from server) */
  documentCount: number;
  startTime: Date;
  endTime: Date;
  /** Normalized position (0-1) on the timeline */
  normalizedPosition: number;
  /** Pixel position from top */
  pixelPosition: number;
  /** Which side of the stem (alternates for visual balance) */
  side: "left" | "right";
  /** True when this bucket renders one card per document (see
   *  `bucketShowsDocumentCards`); the extra cards occupy the slots reserved
   *  after `pixelPosition` (see `bucketSlotCount`), each one two
   *  TIMELINE_ENTRY_GAPs further down on this same side. */
  showsDocumentCards: boolean;
  /** Server bucket data for expansion */
  bucket: TimelineBucket;
}

/**
 * Timeline data from server-side buckets
 */
export interface ServerTimelineData {
  clusters: ServerTimelineCluster[];
  totalDocuments: number;
  timeRange: {
    start: Date;
    end: Date;
    durationMs: number;
  } | null;
}

/**
 * Convert server-side TimelineBucket array to UI-renderable ServerTimelineData.
 *
 * This function transforms aggregated bucket data from the database
 * into clusters that can be rendered on the timeline. Unlike client-side
 * clustering, these buckets already represent grouped documents.
 *
 * @param buckets - Server-side aggregated buckets (from RPC)
 * @param zoomLevel - Current zoom level (0-100)
 * @param containerHeight - Container height in pixels
 * @returns Timeline data ready for rendering
 *
 * @example
 * ```ts
 * const data = bucketsToTimelineClusters(
 *   store.timelineBuckets,
 *   zoomLevel,
 *   containerHeight
 * );
 * ```
 */
export function bucketsToTimelineClusters(
  buckets: TimelineBucket[],
  zoomLevel: ZoomLevel,
  containerHeight: number
): ServerTimelineData {
  if (buckets.length === 0) {
    return {
      clusters: [],
      totalDocuments: 0,
      timeRange: null,
    };
  }

  // Buckets come sorted DESC from server (newest first)
  // Get time range from first (newest) and last (oldest) buckets
  const newestBucket = buckets[0];
  const oldestBucket = buckets[buckets.length - 1];

  const timeRange = {
    start: oldestBucket.bucketStart,
    end: newestBucket.bucketEnd,
    durationMs: newestBucket.bucketEnd.getTime() - oldestBucket.bucketStart.getTime() || 1,
  };

  const totalDocuments = buckets.reduce((sum, b) => sum + b.documentCount, 0);

  // Calculate positions for each bucket
  const clusters = calculateBucketPositions(buckets, timeRange, zoomLevel, containerHeight);

  return {
    clusters,
    totalDocuments,
    timeRange,
  };
}

/**
 * Calculate pixel positions for server-side buckets.
 *
 * Every rendered card — cluster card or document card — takes one slot, and
 * consecutive slots sit a constant TIMELINE_ENTRY_GAP apart regardless of how
 * much time separates them. A bucket that renders document cards reserves one
 * slot per document so the following bucket starts below them.
 */
function calculateBucketPositions(
  buckets: TimelineBucket[],
  timeRange: { start: Date; end: Date; durationMs: number },
  zoomLevel: ZoomLevel,
  containerHeight: number
): ServerTimelineCluster[] {
  if (buckets.length === 0) return [];

  const clusters: ServerTimelineCluster[] = [];
  let slotIndex = 0;

  for (let i = 0; i < buckets.length; i++) {
    const bucket = buckets[i];
    const showsDocumentCards = bucketShowsDocumentCards(bucket.documentCount, zoomLevel);
    const slots = showsDocumentCards ? bucketSlotCount(bucket.documentCount) : 1;

    // Calculate midpoint time for this bucket
    const midTime = new Date((bucket.bucketStart.getTime() + bucket.bucketEnd.getTime()) / 2);

    // Normalized position within time range (0 = newest, 1 = oldest)
    const normalizedPosition =
      timeRange.durationMs > 0
        ? (timeRange.end.getTime() - midTime.getTime()) / timeRange.durationMs
        : 0.5;

    clusters.push({
      id: `bucket-${bucket.bucketStart.toISOString()}`,
      type: bucket.documentCount === 1 ? "single" : "cluster",
      documentCount: bucket.documentCount,
      startTime: bucket.bucketStart,
      endTime: bucket.bucketEnd,
      normalizedPosition,
      pixelPosition: TIMELINE_START_OFFSET + slotIndex * TIMELINE_ENTRY_GAP,
      side: slotIndex % 2 === 0 ? "left" : "right",
      showsDocumentCards,
      bucket,
    });

    slotIndex += slots;
  }

  return clusters;
}

/**
 * Generate date markers for server-side timeline buckets.
 * Places markers at cluster positions with appropriate formatting.
 */
export function generateServerDateMarkers(
  clusters: Pick<
    ServerTimelineCluster,
    "startTime" | "endTime" | "normalizedPosition" | "pixelPosition"
  >[],
  timeRange: { start: Date; end: Date; durationMs: number } | null,
  zoomLevel?: number
): TimelineDateMarker[] {
  if (!clusters.length || !timeRange) return [];

  const markers: TimelineDateMarker[] = [];
  const daysSpan = timeRange.durationMs / (24 * 60 * 60 * 1000);
  const yearsSpan = daysSpan / 365;

  // Determine format based on time span
  let formatStr: string;
  if (yearsSpan > 2) {
    formatStr = "yyyy";
  } else if (yearsSpan > 0.5) {
    formatStr = "MMM yyyy";
  } else if (daysSpan > 30) {
    formatStr = "MMM d";
  } else if (daysSpan > 1) {
    formatStr = "MMM d";
  } else {
    formatStr = "h:mm a";
  }

  // At high zoom, override with day-level labels regardless of total span
  if (zoomLevel !== undefined && zoomLevel >= 70) {
    formatStr = "MMM d, yyyy";
  }

  // Deduplicate by formatted label
  const seenLabels = new Set<string>();

  for (const cluster of clusters) {
    const midTime = new Date((cluster.startTime.getTime() + cluster.endTime.getTime()) / 2);
    const label = format(midTime, formatStr);

    if (seenLabels.has(label)) continue;
    seenLabels.add(label);

    markers.push({
      date: midTime,
      label,
      normalizedPosition: cluster.normalizedPosition,
      pixelPosition: cluster.pixelPosition,
      isMinor: false,
    });
  }

  return markers;
}
